"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  registerCustomer,
  loginCustomer,
  fetchProfile,
  verifyMfa,
  type CustomerUser,
  type PWAddress,
} from "@/lib/api/customer";
import { analytics } from "@/lib/analytics/core";
import { hashUserIdForAnalytics } from "@/lib/analytics/hashUserIdForAnalytics";
import { clearPrescriptionsCache } from "@/lib/prescriptions-cache";
import { clearAddressesCache } from "@/lib/addresses-cache";

/* ── Types ── */
export type User = {
  id: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: PWAddress;
  patientId: string;
};

export type MfaDeviceType = "totp" | "webauthn" | "sms" | "email" | "static";

type AuthResult = Promise<{ ok: boolean; error?: string; mfaRequired?: boolean }>;

type AuthContextValue = {
  user: User | null;
  token: string | null;
  patientId: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  mfaPending: boolean;
  mfaSessionId: string | null;
  mfaMethods: MfaDeviceType[];
  mfaCodeSent: boolean;
  mfaAutoMethod: MfaDeviceType | null;
  mfaEmail: string | null;
  login: (email: string, password: string) => AuthResult;
  completeMfaLogin: (
    sessionId: string,
    code: string,
    method: MfaDeviceType,
    webauthnResponse?: Record<string, unknown>,
  ) => AuthResult;
  cancelMfa: () => void;
  register: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    phone?: string,
    address?: PWAddress,
  ) => AuthResult;
  logout: () => void;
};

const TOKEN_KEY = "bp-auth-token";
const SESSION_KEY = "bp-session";
const PATIENT_KEY = "bp-patient-id";
const MFA_DEFAULT_PREFIX = "bp-mfa-default-";

const AuthContext = createContext<AuthContextValue | null>(null);

/* ── Helpers ── */
function loadToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function saveToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

function loadPatientId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(PATIENT_KEY);
  } catch {
    return null;
  }
}

function savePatientId(id: string | null) {
  try {
    if (id) localStorage.setItem(PATIENT_KEY, id);
    else localStorage.removeItem(PATIENT_KEY);
  } catch { /* ignore */ }
}

function loadCachedSession(): User | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function saveCachedSession(user: User | null) {
  try {
    if (user) localStorage.setItem(SESSION_KEY, JSON.stringify(user));
    else localStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
}

function mapApiUser(u: CustomerUser, pid: string): User {
  return {
    id: u.id || pid,
    name: u.name || `${u.first_name} ${u.last_name}`.trim(),
    email: u.email,
    firstName: u.first_name,
    lastName: u.last_name,
    phone: u.phone ?? "",
    address: u.address ?? {},
    patientId: pid || u.id,
  };
}

/* ── Provider ── */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSessionId, setMfaSessionId] = useState<string | null>(null);
  const [mfaMethods, setMfaMethods] = useState<MfaDeviceType[]>([]);
  const [mfaCodeSent, setMfaCodeSent] = useState(false);
  const [mfaAutoMethod, setMfaAutoMethod] = useState<MfaDeviceType | null>(null);
  const [mfaEmail, setMfaEmail] = useState<string | null>(null);

  const clearAuth = useCallback(() => {
    // Drop any cached personal data the moment the session ends or switches accounts.
    clearPrescriptionsCache();
    clearAddressesCache();
    setUser(null);
    setToken(null);
    setPatientId(null);
    setMfaPending(false);
    setMfaSessionId(null);
    setMfaMethods([]);
    setMfaCodeSent(false);
    setMfaAutoMethod(null);
    setMfaEmail(null);
    saveToken(null);
    savePatientId(null);
    saveCachedSession(null);
  }, []);

  useEffect(() => {
    const savedToken = loadToken();
    const savedPid = loadPatientId();
    if (!savedToken) {
      setLoading(false);
      return;
    }

    const cached = loadCachedSession();
    if (cached) {
      setUser(cached);
      setToken(savedToken);
      setPatientId(savedPid);
    }

    fetchProfile(savedToken).then((res) => {
      if (res.ok) {
        const mapped = mapApiUser(res.data, savedPid ?? res.data.id);
        setUser(mapped);
        setToken(savedToken);
        setPatientId(mapped.patientId);
        saveCachedSession(mapped);
        savePatientId(mapped.patientId);
      } else {
        clearAuth();
      }
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const PROTECTED_PREFIXES = ["/account"];

    const onTokenExpired = () => {
      clearAuth();
      if (typeof window === "undefined") return;
      const path = window.location.pathname;
      const isProtectedPage = PROTECTED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
      if (isProtectedPage) {
        window.location.href = "/login?expired=1";
      }
    };
    window.addEventListener("bp-token-expired", onTokenExpired);
    return () => window.removeEventListener("bp-token-expired", onTokenExpired);
  }, [clearAuth]);

  const login = useCallback(
    async (email: string, password: string): AuthResult => {
      let preferredMethod: string | undefined;
      try {
        preferredMethod = localStorage.getItem(`${MFA_DEFAULT_PREFIX}${email.toLowerCase()}`) ?? undefined;
      } catch { /* ignore */ }

      const res = await loginCustomer(email, password, preferredMethod);
      if (!res.ok) return { ok: false, error: res.error };

      const data = res.data as Record<string, unknown>;

      if (data.mfa_required) {
        setMfaPending(true);
        setMfaSessionId(data.mfa_session_id as string);
        setMfaMethods((data.methods as MfaDeviceType[]) ?? []);
        setMfaCodeSent(!!data.code_sent);
        setMfaAutoMethod((data.auto_method as MfaDeviceType) ?? null);
        setMfaEmail(email);
        return { ok: true, mfaRequired: true };
      }

      const typedData = data as unknown as { token: string; patient_id: string; user: CustomerUser };
      const pid = typedData.patient_id;
      const mapped = mapApiUser(typedData.user, pid);
      setUser(mapped);
      setToken(typedData.token);
      setPatientId(pid);
      saveToken(typedData.token);
      savePatientId(pid);
      saveCachedSession(mapped);
      analytics.track("login", { method: "email" });
      analytics.identify(await hashUserIdForAnalytics(pid));
      return { ok: true };
    },
    [],
  );

  const completeMfaLogin = useCallback(
    async (sessionId: string, code: string, method: MfaDeviceType, webauthnResponse?: Record<string, unknown>): AuthResult => {
      const res = await verifyMfa(sessionId, code, method, webauthnResponse);
      if (!res.ok) return { ok: false, error: res.error };

      const pid = res.data.patient_id;
      const mapped = mapApiUser(res.data.user as CustomerUser, pid);
      setUser(mapped);
      setToken(res.data.token);
      setPatientId(pid);
      setMfaPending(false);
      setMfaSessionId(null);
      setMfaMethods([]);
      saveToken(res.data.token);
      savePatientId(pid);
      saveCachedSession(mapped);
      analytics.track("login", { method: "mfa" });
      analytics.identify(await hashUserIdForAnalytics(pid));
      return { ok: true };
    },
    [],
  );

  const cancelMfa = useCallback(() => {
    setMfaPending(false);
    setMfaSessionId(null);
    setMfaMethods([]);
    setMfaAutoMethod(null);
    setMfaEmail(null);
  }, []);

  const register = useCallback(
    async (
      firstName: string,
      lastName: string,
      email: string,
      password: string,
      phone?: string,
      address?: PWAddress,
    ): AuthResult => {
      const res = await registerCustomer(firstName, lastName, email, password, phone, address);
      if (!res.ok) return { ok: false, error: res.error };

      const pid = res.data.patient_id;
      const mapped = mapApiUser(res.data.user, pid);
      setUser(mapped);
      setToken(res.data.token);
      setPatientId(pid);
      saveToken(res.data.token);
      savePatientId(pid);
      saveCachedSession(mapped);
      analytics.track("sign_up", { method: "email" });
      analytics.identify(await hashUserIdForAnalytics(pid));
      return { ok: true };
    },
    [],
  );

  const logout = useCallback(() => {
    analytics.track("logout", {});
    analytics.reset();
    clearAuth();
    // Clear HttpOnly pw_token + pw_csrf cookies set by the cookie-auth flow.
    // Fire-and-forget — if it fails the localStorage session is already gone.
    fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => undefined);
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        patientId,
        isAuthenticated: !!user,
        loading,
        mfaPending,
        mfaSessionId,
        mfaMethods,
        mfaCodeSent,
        mfaAutoMethod,
        mfaEmail,
        login,
        completeMfaLogin,
        cancelMfa,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
